import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { ElementData } from "../Data/ElementData";
import { ObstacleType } from "../Data/Obstacles";
import { RegionData } from "../Data/RegionData";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { TicketColourSuffixes } from "../GameObjects/TicketBooth";
import { BoxCollider } from "../Physics/BoxCollider";
import { CompoundBoxCollider } from "../Physics/CompoundBoxCollider";

export enum RegionType {
    MEXICO,
    RUSSIA,
    EGYPT,
    CHINA,
    ENGLAND,
    INDIA,
    AUSTRALIA,
    JAPAN,
    NUM_ELEMENTS,
}

export class Regions {
    private static _data: ReadonlyArray<RegionData>;
    private static _clouds: ReadonlyArray<ElementData>;

    public static get data(): ReadonlyArray<RegionData> {
        if (this._data === undefined) {
            this._data = Regions.initData();
        }
        return this._data;
    }

    public static get clouds(): ReadonlyArray<ElementData> {
        if (this._clouds === undefined) {
            this._clouds = Regions.initClouds();
        }
        return this._clouds;
    }

    public static get activeData(): RegionData {
        return this.data[AppProgress.activeLevel];
    }

    private static initData(): ReadonlyArray<RegionData> {
        const data: RegionData[] = new Array<RegionData>(RegionType.NUM_ELEMENTS);

        data[RegionType.AUSTRALIA] = new RegionData(
            new ElementData("ground-00"),
            [
                new ElementData("aus-bg-00", "terrain-08"),
                new ElementData("aus-bg-01", "terrain-08"),
                new ElementData("aus-bg-02", "terrain-09"),
                new ElementData("aus-bg-03", "terrain-08"),
            ],
            [
                new ElementData("aus-dec-00", "terrain-00"),
                new ElementData("aus-dec-01", "terrain-00"),
                new ElementData("aus-dec-02", "terrain-00"),
                new ElementData("aus-dec-03", "terrain-00"),
                new ElementData("aus-dec-04", "terrain-00"),
            ],
            [
                new ElementData("aus-gen-00", "terrain-00", true, true, new CompoundBoxCollider([
                    new BoxCollider(20, 395, -180, 0),
                    new BoxCollider(75, 190, -320, -180),
                ])),
                new ElementData("aus-gen-01", "terrain-00", true, true, new CompoundBoxCollider([
                    new BoxCollider(45, 200, -80, 0),
                    new BoxCollider(50, 150, -130, -80),
                    new BoxCollider(80, 120, -165, -130),
                ])),
                new ElementData("aus-gen-02", "terrain-00", true, true, new CompoundBoxCollider([
                    new BoxCollider(45, 125, -250, 0),
                    new BoxCollider(15, 150, -420, -250),
                ])),
                new ElementData("aus-gen-03", "terrain-00", true, true),
            ],
            [
                new ElementData("aus-uni-00", "terrain-00", false, true, new BoxCollider(25, 430, -225, 0),
                    Constants.FUN_FACT_GENERIC_PLACEHOLDER),
                new ElementData("aus-uni-01", "terrain-00", false, true, new CompoundBoxCollider([
                    new BoxCollider(35, 305, -225, 0),
                    new BoxCollider(150, 195, -275, -225),
                ]), Constants.FUN_FACT_GENERIC_PLACEHOLDER),
                new ElementData("aus-uni-02", "terrain-00", false, true, new CompoundBoxCollider([
                    new BoxCollider(25, 465, -235, 0),
                    new BoxCollider(75, 395, -290, -235),
                ]), Constants.FUN_FACT_GENERIC_PLACEHOLDER),
                new ElementData("aus-uni-03", "terrain-00", false, true, new CompoundBoxCollider([
                    new BoxCollider(40, 265, -200, 0),
                    new BoxCollider(210, 240, -270, -200),
                ]), Constants.FUN_FACT_GENERIC_PLACEHOLDER),
            ],
            [
                ObstacleType.BOOMERANG,
            ],
            new PIXI.Point(500, 750),
            new ElementData("aus-flag", "terrain-00", false),
            new ElementData("aus-ticket", "terrain-common", false),
            [ TicketColourSuffixes.BLUE, TicketColourSuffixes.WHITE, TicketColourSuffixes.RED ],
        );

        data[RegionType.CHINA] = new RegionData(
            new ElementData("ground-00"),
            [
                new ElementData("chi-bg-00", "terrain-09"),
                new ElementData("chi-bg-01", "terrain-10"),
                new ElementData("chi-bg-02", "terrain-10"),
                new ElementData("chi-bg-03", "terrain-10"),
            ],
            [
                new ElementData("chi-dec-00", "terrain-01"),
                new ElementData("chi-dec-01", "terrain-01"),
                new ElementData("chi-dec-02", "terrain-01"),
                new ElementData("chi-dec-03", "terrain-01"),
            ],
            [
                new ElementData("chi-gen-00", "terrain-01", false, true, new BoxCollider(35, 180, -260, 0)),
                new ElementData("chi-gen-01", "terrain-01", false, true, new BoxCollider(35, 265, -175, 0)),
                new ElementData("chi-gen-02", "terrain-01", false, true, new BoxCollider(20, 160, -175, 0)),
            ],
            [
                new ElementData("chi-uni-00", "terrain-01", false, true, new CompoundBoxCollider([
                    new BoxCollider(45, 90, -175, 0),
                    new BoxCollider(90, 350, -225, 0),
                    new BoxCollider(350, 400, -175, 0),
                ]), VoiceOver.FF_CHI_BEIHEI),
                new ElementData("chi-uni-01", "terrain-01", false, true, new CompoundBoxCollider([
                    new BoxCollider(20, 90, -75, 0),
                    new BoxCollider(90, 330, -310, 0),
                    new BoxCollider(330, 410, -75, 0),
                ]), VoiceOver.FF_CHI_DRUM),
                new ElementData("chi-uni-02", "terrain-01", false, true, new CompoundBoxCollider([
                    new BoxCollider(50, 300, -185, 0),
                    new BoxCollider(90, 255, -340, -185),
                ]), VoiceOver.FF_CHI_BELL),
                new ElementData("chi-uni-03", "terrain-08", false, true, new CompoundBoxCollider([
                    new BoxCollider(35, 290, -250, 0),
                    new BoxCollider(290, 490, -300, 0),
                    new BoxCollider(490, 720, -400, 0),
                ]), "ff-gen-chi-02"),
            ],
            [
                ObstacleType.LANTERN,
            ],
            new PIXI.Point(300, 600),
            new ElementData("chi-flag", "terrain-01", false),
            new ElementData("chi-ticket", "terrain-common", false),
            [ TicketColourSuffixes.RED, TicketColourSuffixes.YELLOW ],
        );

        data[RegionType.EGYPT] = new RegionData(
            new ElementData("ground-01"),
            [
                new ElementData("egy-bg-00", "terrain-10"),
                new ElementData("egy-bg-01", "terrain-10"),
                new ElementData("egy-bg-02", "terrain-10"),
                new ElementData("egy-bg-03", "terrain-10"),
                new ElementData("egy-bg-04", "terrain-10"),
                new ElementData("egy-bg-05", "terrain-10"),
            ],
            [
                new ElementData("egy-dec-00", "terrain-02"),
                new ElementData("egy-dec-01", "terrain-02"),
                new ElementData("egy-dec-02", "terrain-02"),
            ],
            [
                new ElementData("egy-gen-00", "terrain-02", true, true, new BoxCollider(30, 280, -135, 0)),
                new ElementData("egy-gen-01", "terrain-02", true, true, new CompoundBoxCollider([
                    new BoxCollider(15, 90, -140, 0),
                    new BoxCollider(90, 160, -230, 0),
                ])),
                new ElementData("egy-gen-02", "terrain-02", true, true, new BoxCollider(15, 140, -190, 0)),
            ],
            [
                new ElementData("egy-uni-00", "terrain-02", false, true, new CompoundBoxCollider([
                    new BoxCollider(15, 225, -60, 0),
                    new BoxCollider(55, 190, -150, -60),
                    new BoxCollider(110, 180, -290, -150),
                ]), Constants.FUN_FACT_GENERIC_PLACEHOLDER),
                new ElementData("egy-uni-01", "terrain-02", false, true, new CompoundBoxCollider([
                    new BoxCollider(15, 140, -80, 0),
                    new BoxCollider(55, 100, -375, -80),
                ]), VoiceOver.FF_EGY_OBELISK),
                new ElementData("egy-uni-02", "terrain-02", false, true, new CompoundBoxCollider([
                    new BoxCollider(15, 170, -70, 0),
                    new BoxCollider(170, 310, -250, 0),
                    new BoxCollider(310, 470, -135, 0),
                ]), VoiceOver.FF_EGY_SPHINX),
                new ElementData("egy-uni-03", "terrain-02", false, true, new CompoundBoxCollider([
                    new BoxCollider(30, 495, -125, 0),
                    new BoxCollider(150, 440, -155, -125),
                    new BoxCollider(190, 390, -215, -155),
                    new BoxCollider(240, 340, -290, -215),
                ]), VoiceOver.FF_EGY_OPERA),
            ],
            [
                ObstacleType.SCARAB,
            ],
            new PIXI.Point(300, 600),
            new ElementData("egy-flag", "terrain-02", false),
            new ElementData("egy-ticket", "terrain-common", false),
            [ TicketColourSuffixes.RED, TicketColourSuffixes.WHITE, TicketColourSuffixes.BLACK ],
        );

        data[RegionType.ENGLAND] = new RegionData(
            new ElementData("ground-00"),
            [
                new ElementData("eng-bg-00", "terrain-11"),
                new ElementData("eng-bg-01", "terrain-11"),
                new ElementData("eng-bg-02", "terrain-11" /* , false, false, undefined, VoiceOver.FF_ENG_BRIDGE */),
                new ElementData("eng-bg-03", "terrain-11"),
            ],
            [
                new ElementData("eng-dec-00", "terrain-03"),
                new ElementData("eng-dec-00", "terrain-03"),
                new ElementData("eng-dec-00", "terrain-03"),
                new ElementData("eng-dec-01", "terrain-03"),
                new ElementData("eng-dec-02", "terrain-03"),
                new ElementData("eng-dec-02", "terrain-03"),
                new ElementData("eng-dec-02", "terrain-03"),
                new ElementData("eng-dec-02", "terrain-03"),
                new ElementData("eng-dec-02", "terrain-03"),
            ],
            [
                new ElementData("eng-gen-00", "terrain-03", true, true, new BoxCollider(25, 240, -190, 0)),
                new ElementData("eng-gen-01", "terrain-03", true, true, new BoxCollider(10, 105, -165, 0)),
                new ElementData("eng-gen-02", "terrain-03", false, true, new BoxCollider(15, 155, -105, 0)),
            ],
            [
                new ElementData("eng-uni-00", "terrain-03", false, true, new CompoundBoxCollider([
                    new BoxCollider(30, 430, -190, 0),
                    new BoxCollider(125, 330, -270, -190),
                ]), VoiceOver.FF_ENG_TOWER),
                new ElementData("eng-uni-01", "terrain-03", false, true, new CompoundBoxCollider([
                    new BoxCollider(15, 45, -285, -100),
                    new BoxCollider(45, 85, -320, -70),
                    new BoxCollider(85, 270, -350, 0),
                    new BoxCollider(270, 315, -320, -70),
                    new BoxCollider(315, 350, -285, -100),
                ]), VoiceOver.FF_ENG_EYE),
                new ElementData("eng-uni-02", "terrain-03", false, true, new CompoundBoxCollider([
                    new BoxCollider(25, 100, -305, 0),
                    new BoxCollider(40, 80, -380, -305),
                ]), VoiceOver.FF_ENG_BIGBEN),
            ],
            [
                ObstacleType.PIGEON,
            ],
            new PIXI.Point(300, 600),
            new ElementData("eng-flag", "terrain-03", false),
            new ElementData("eng-ticket", "terrain-common", false),
            [ TicketColourSuffixes.RED, TicketColourSuffixes.BLUE, TicketColourSuffixes.WHITE ],
        );

        data[RegionType.INDIA] = new RegionData(
            new ElementData("ground-00"),
            [
                new ElementData("ind-bg-00", "terrain-11"),
                new ElementData("ind-bg-01", "terrain-11"),
                new ElementData("ind-bg-02", "terrain-11"),
            ],
            [
                new ElementData("ind-dec-00", "terrain-04"),
                new ElementData("ind-dec-01", "terrain-04"),
                new ElementData("ind-dec-02", "terrain-04"),
                new ElementData("ind-dec-03", "terrain-04"),
                new ElementData("ind-dec-04", "terrain-04"),
                new ElementData("ind-dec-05", "terrain-04"),
            ],
            [
                new ElementData("ind-gen-00", "terrain-04", true, true, new BoxCollider(10, 80, -95, 0)),
                new ElementData("ind-gen-01", "terrain-04", false, true, new BoxCollider(35, 205, -240, 0)),
                new ElementData("ind-gen-02", "terrain-04", true, true, new BoxCollider(10, 140, -290, 0)),
                new ElementData("ind-gen-03", "terrain-04", false, true, new BoxCollider(0, 280, -155, 0)),
            ],
            [
                new ElementData("ind-uni-00", "terrain-04", false, true, new CompoundBoxCollider([
                    new BoxCollider(15, 360, -215, 0),
                    new BoxCollider(60, 290, -270, -215),
                    new BoxCollider(140, 210, -340, -270),
                ]), VoiceOver.FF_IND_LODHI),
                new ElementData("ind-uni-01", "terrain-04", false, true, new BoxCollider(40, 355, -230, 0),
                    VoiceOver.FF_IND_JAMAMASJID),
                new ElementData("ind-uni-02", "terrain-04", false, true, new CompoundBoxCollider([
                    new BoxCollider(80, 180, -200, -50),
                    new BoxCollider(245, 320, -330, -50),
                    new BoxCollider(320, 380, -225, -50),
                    new BoxCollider(380, 430, -125, -50),
                    new BoxCollider(485, 565, -190, -50),
                ]), VoiceOver.FF_IND_JANTARMANTAR),
            ],
            [
                ObstacleType.DYE,
            ],
            new PIXI.Point(300, 600),
            new ElementData("ind-flag", "terrain-04", false),
            new ElementData("ind-ticket", "terrain-common", false),
            [ TicketColourSuffixes.ORANGE, TicketColourSuffixes.WHITE, TicketColourSuffixes.GREEN ],
        );

        data[RegionType.JAPAN] = new RegionData(
            new ElementData("ground-00"),
            [
                new ElementData("jap-bg-00", "terrain-12"),
                new ElementData("jap-bg-01", "terrain-12"),
                new ElementData("jap-bg-02", "terrain-12"),
            ],
            [
                new ElementData("jap-dec-00", "terrain-05"),
                new ElementData("jap-dec-01", "terrain-05"),
                new ElementData("jap-dec-02", "terrain-05"),
                new ElementData("jap-dec-03", "terrain-05"),
                new ElementData("jap-dec-04", "terrain-05"),
                new ElementData("jap-dec-05", "terrain-05"),
                new ElementData("jap-dec-06", "terrain-05"),
            ],
            [
                new ElementData("jap-gen-00", "terrain-05", true, true, new CompoundBoxCollider([
                    new BoxCollider(15, 140, -140, 0),
                    new BoxCollider(60, 90, -190, -140),
                ])),
                new ElementData("jap-gen-01", "terrain-05", true, true, new CompoundBoxCollider([
                    new BoxCollider(10, 180, -100, -0),
                    new BoxCollider(35, 155, -300, -100),
                ])),
                new ElementData("jap-gen-02", "terrain-05", true, true, new CompoundBoxCollider([
                    new BoxCollider(25, 275, -170, -0),
                    new BoxCollider(45, 130, -255, -170),
                ])),
            ],
            [
                new ElementData("jap-uni-00", "terrain-08", false, true, new CompoundBoxCollider([
                    new BoxCollider(75, 515, -175, 0),
                    new BoxCollider(155, 430, -260, -175),
                ]), VoiceOver.FF_JAP_SUMO),
                new ElementData("jap-uni-01", "terrain-05", false, true, new CompoundBoxCollider([
                    new BoxCollider(25, 300, -160, 0),
                    new BoxCollider(50, 285, -275, -160),
                ]), Constants.FUN_FACT_GENERIC_PLACEHOLDER),
                new ElementData("jap-uni-02", "terrain-05", false, true, new BoxCollider(30, 400, -220, 0)),
                new ElementData("jap-gen-03", "terrain-05", false, true, new BoxCollider(25, 310, -240, 0),
                    VoiceOver.FF_JAP_YOYOGI),
            ],
            [
                ObstacleType.DINOSAUR,
                ObstacleType.FUUJIN,
            ],
            new PIXI.Point(500, 900),
            new ElementData("jap-flag", "terrain-05", false),
            new ElementData("jap-ticket", "terrain-common", false),
            [ TicketColourSuffixes.WHITE, TicketColourSuffixes.RED ],
            [
                new ElementData("circus-full", "terrain-13", false, false),
            ],
        );

        data[RegionType.MEXICO] = new RegionData(
            new ElementData("ground-00"),
            [
                new ElementData("mex-bg-00", "terrain-12"),
                new ElementData("mex-bg-01", "terrain-12"),
                new ElementData("mex-bg-02", "terrain-12"),
            ],
            [
                new ElementData("mex-dec-00", "terrain-06"),
                new ElementData("mex-dec-01", "terrain-06"),
                new ElementData("mex-dec-02", "terrain-06"),
                new ElementData("mex-dec-03", "terrain-06"),
            ],
            [
                new ElementData("mex-gen-00", "terrain-06", false, true, new BoxCollider(15, 335, -160, 0)),
                new ElementData("mex-gen-01", "terrain-06", true, true, new BoxCollider(35, 160, -220, 0)),
                new ElementData("mex-gen-02", "terrain-06", true, true, new BoxCollider(15, 185, -155, 0)),
            ],
            [
                new ElementData("mex-uni-00", "terrain-06", false, true, new CompoundBoxCollider([
                    new BoxCollider(30, 130, -30, 0),
                    new BoxCollider(60, 110, -95, -30),
                    new BoxCollider(70, 95, -260, -95),
                    new BoxCollider(45, 105, -345, -260),
                ]), VoiceOver.FF_MEX_ANGEL),
                new ElementData("mex-uni-01", "terrain-06", false, true, new BoxCollider(50, 420, -195, 0),
                    Constants.FUN_FACT_GENERIC_PLACEHOLDER),
                new ElementData("mex-uni-02", "terrain-06", false, true, new CompoundBoxCollider([
                    new BoxCollider(30, 285, -180, 0),
                    new BoxCollider(285, 525, -215, 0),
                ]), Constants.FUN_FACT_GENERIC_PLACEHOLDER),
                new ElementData("mex-uni-03", "terrain-06", false, true, new CompoundBoxCollider([
                    new BoxCollider(45, 135, -160, 0),
                    new BoxCollider(135, 280, -205, 0),
                    new BoxCollider(280, 335, -320, 0),
                    new BoxCollider(335, 465, -205, 0),
                    new BoxCollider(465, 570, -175, 0),
                ]), VoiceOver.FF_MEX_ZOCALOS),
            ],
            [
                ObstacleType.PARROT,
                ObstacleType.PALETAS,
            ],
            new PIXI.Point(300, 600),
            new ElementData("mex-flag", "terrain-06", false),
            new ElementData("mex-ticket", "terrain-common", false),
            [ TicketColourSuffixes.GREEN, TicketColourSuffixes.WHITE, TicketColourSuffixes.RED ],
        );

        data[RegionType.RUSSIA] = new RegionData(
            new ElementData("ground-00"),
            [
                new ElementData("rus-bg-00", "terrain-13"),
                new ElementData("rus-bg-01", "terrain-13"),
                new ElementData("rus-bg-02", "terrain-13"),
                new ElementData("rus-bg-03", "terrain-13"),
            ],
            [
                new ElementData("rus-dec-00", "terrain-07"),
                new ElementData("rus-dec-01", "terrain-07"),
                new ElementData("rus-dec-02", "terrain-07"),
                new ElementData("rus-dec-03", "terrain-07"),
            ],
            [
                new ElementData("rus-gen-00", "terrain-07", true, true, new BoxCollider(30, 235, -195, 0)),
                new ElementData("rus-gen-01", "terrain-07", true, true, new BoxCollider(20, 140, -280, 0)),
                new ElementData("rus-gen-02", "terrain-07", true, true, new CompoundBoxCollider([
                    new BoxCollider(25, 195, -170, 0),
                    new BoxCollider(75, 140, -205, -170),
                ])),
            ],
            [
                new ElementData("rus-uni-00", "terrain-07", false, true, new CompoundBoxCollider([
                    new BoxCollider(50, 665, -50, 0),
                    new BoxCollider(120, 470, -90, -50),
                    new BoxCollider(350, 470, -140, -90),
                    new BoxCollider(410, 490, -180, -140),
                    new BoxCollider(450, 520, -240, -180),
                    new BoxCollider(505, 530, -320, -240),
                    new BoxCollider(530, 550, -380, -290),
                ]), VoiceOver.FF_RUS_SPACE),
                new ElementData("rus-uni-01", "terrain-07", false, true, new CompoundBoxCollider([
                    new BoxCollider(55, 130, -245, 0),
                    new BoxCollider(130, 380, -315, 0),
                    new BoxCollider(380, 460, -245, 0),
                    new BoxCollider(170, 350, -335, -315),
                    new BoxCollider(210, 300, -355, -335),
                ]), VoiceOver.FF_RUS_BOLSHOI),
                new ElementData("rus-uni-02", "terrain-09", false, true, new CompoundBoxCollider([
                    new BoxCollider(75, 395, -300, 0),
                    new BoxCollider(185, 315, -360, -300),
                    new BoxCollider(210, 280, -420, -360),
                ]), VoiceOver.FF_RUS_CATHEDRAL),
                new ElementData("rus-uni-03", "terrain-09", true, true, new CompoundBoxCollider([
                    new BoxCollider(60, 380, -265, 0),
                    new BoxCollider(120, 210, -305, -265),
                    new BoxCollider(210, 255, -420, -265),
                ]), VoiceOver.FF_RUS_REDSQUARE),
            ],
            [
                ObstacleType.SPUTNIK,
            ],
            new PIXI.Point(300, 600),
            new ElementData("rus-flag", "terrain-07", false),
            new ElementData("rus-ticket", "terrain-common", false),
            [ TicketColourSuffixes.WHITE, TicketColourSuffixes.BLUE, TicketColourSuffixes.RED ],
        );

        return data;
    }

    private static initClouds(): ReadonlyArray<ElementData> {
        const clouds: ElementData[] = new Array<ElementData>(5);
        for (let i = 0; i < clouds.length; ++i) {
            clouds[i] = (new ElementData(
                "cloud-0" + i,
                "terrain-common",
                true,
                false,
            ));
        }
        return clouds;
    }
}
